/**
 * Collections Home A/B test (OMG-2581).
 *
 * New English purchasers are split 50/50 between the current member home
 * (`nmh`) and the collections page as home (`collections`, the experience
 * non-English members already get). The arm is assigned and frozen by
 * omg-api (GET /experiments/member) and this module turns "locale + arm +
 * is the arm known yet" into ONE answer every home-shaped decision in the
 * app reads: which page /home renders, whether the collections page shows a
 * way back to the member home, and whether the Article and Date Night pages
 * exist for this member (they belong to the member home and are removed for
 * the collections arm entirely, Rob 2026-09-14).
 *
 * The arm names are the wire contract with omg-api `models/experiments.ts`
 * (MEMBER_HOME_ARMS); the server's experiment key (`member_home_2026`) never
 * reaches this app. A value the server sends that this module does not
 * recognise reads as "not in the test" (isMemberHomeArm), i.e. today's home.
 */

export type MemberHomeArm = "nmh" | "collections";

/** Segment identify trait carrying the arm (new_member_home source). Must
 *  be in scrubEvent's TRAIT_ALLOWLIST or the middleware drops it silently. */
export const MEMBER_HOME_ARM_TRAIT = "member_home_arm";

/** Where a member whose home is the collections page lands. */
export const COLLECTIONS_HOME_PATH = "/collection/essentials";

/**
 * `pending` = the answer is not knowable yet: the locale stores have not been
 * reconciled (first render), or the member is English and the arm request is
 * still in flight. Render a loader, not either page, so nobody sees the
 * member home for a frame and then gets yanked to the collections page (or
 * the reverse).
 */
export type HomeExperience = MemberHomeArm | "pending";

export const isMemberHomeArm = (value: unknown): value is MemberHomeArm =>
  value === "nmh" || value === "collections";

/**
 * The one decision. `locale` is LocaleContext's `settledLocale` (null until
 * the stores are reconciled), never i18next's first guess. Order matters:
 *  1. No settled locale yet → pending (one effect tick, no network).
 *  2. Non-English members get the collections page regardless of the test —
 *     that is today's rule (NewMemberHome's locale redirect) and it does not
 *     wait on the arm request.
 *  3. English members wait for the arm while it is pending.
 *  4. `collections` is the only value that changes anything; `nmh`, null
 *     (not enrolled, test off, request failed) and anything unrecognised all
 *     mean today's member home. Failing OPEN to the current experience is the
 *     point: the test must never cost a member their home page.
 */
export const resolveHomeExperience = ({
  locale,
  arm,
  pending,
}: {
  locale: string | null;
  arm: unknown;
  pending: boolean;
}): HomeExperience => {
  if (locale === null) return "pending";
  if (locale !== "en") return "collections";
  if (pending) return "pending";
  return arm === "collections" ? "collections" : "nmh";
};
